import { useEffect, useRef } from 'react';

// Vector tick-field: strokes lean toward cursor; idle sine drift.
export default function TickField({ density = 26 }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });
  const raf = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w, h, dpr;
    const setSize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();
    window.addEventListener('resize', setSize);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = e.clientX - rect.left;
      mouse.current.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.current.x = -9999; mouse.current.y = -9999; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    let t0 = performance.now();
    const draw = (now) => {
      const time = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);
      const step = density;
      const cols = Math.ceil(w / step);
      const rows = Math.ceil(h / step);
      const len = 12;
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          const x = c * step;
          const y = r * step;
          const dx = mouse.current.x - x;
          const dy = mouse.current.y - y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          let angle;
          if (dist < 260) {
            angle = Math.atan2(dy, dx);
            const pull = 1 - dist / 260;
            angle += Math.sin(time * 0.6 + r * 0.15 + c * 0.15) * 0.12 * (1 - pull);
          } else {
            angle = Math.sin(time * 0.35 + r * 0.22 + c * 0.18) * 0.6 + Math.PI / 4;
          }
          const alpha = dist < 260 ? 0.55 : 0.22;
          ctx.strokeStyle = `rgba(60, 9, 108, ${alpha})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x - Math.cos(angle) * len / 2, y - Math.sin(angle) * len / 2);
          ctx.lineTo(x + Math.cos(angle) * len / 2, y + Math.sin(angle) * len / 2);
          ctx.stroke();
        }
      }
      raf.current = requestAnimationFrame(draw);
    };
    raf.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', setSize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      data-testid="tick-field"
    />
  );
}
